import type { ScheduleEntryType } from '../../types';
import { useAuthStore } from '../../stores/authStore';

export const ENTRY_TYPE_COLORS: Record<ScheduleEntryType, string> = {
  job: '#107C10',
  booking: '#CA5010',
  shift: '#0F6CBD',
  event: '#8E8E93',
  personal: '#ADB5BD',
  block: '#DEE2E6',
  class: '#8B5CF6',
  subscribed: '#94A3B8',
};

export const ENTRY_TYPE_LABELS: Record<ScheduleEntryType, string> = {
  job: 'Job',
  booking: 'Booking',
  shift: 'Shift',
  event: 'Event',
  personal: 'Personal',
  block: 'Block',
  class: 'Class',
  subscribed: 'Subscribed',
};

export interface ScheduleUser {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
  employment_type: string;
}

// ── A2 P4 — business-tz READ layer (admin) ───────────────────────────────────
// A stored schedule_entries.start_at/end_at is a true UTC instant (post-P1/P3).
// Rendering/positioning it must use the BUSINESS tz, never the device tz. These
// zero-dep Intl helpers mirror mobile's scheduleUtils read layer. Default tz is
// read from the auth store (singleton; same store P2's admin writers read); an
// optional `tz` param overrides it for tests. DST-correct via Intl per date.

/** Parts of a stored instant, expressed in the business tz. */
export function instantToBusinessParts(
  iso: string,
  tz: string | null = useAuthStore.getState().business?.timezone ?? null,
): { year: number; month: number; day: number; hour: number; minute: number } {
  const zone = tz ?? 'America/Toronto';
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
  const p = Object.fromEntries(dtf.formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return { year: +p.year, month: +p.month, day: +p.day, hour: +p.hour, minute: +p.minute };
}

/** Format a stored instant as a business-tz 12h time string (e.g., "9:30 AM"). */
export function formatInstantInTz(iso: string, tz?: string | null): string {
  const { hour, minute } = instantToBusinessParts(iso, tz);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return `${h12}:${String(minute).padStart(2, '0')} ${ampm}`;
}

/** Minutes-into-day of a stored instant, in the business tz (for grid placement). */
export function businessMinutesIntoDay(iso: string, tz?: string | null): number {
  const { hour, minute } = instantToBusinessParts(iso, tz);
  return hour * 60 + minute;
}

/**
 * Business-tz calendar date of a stored instant ("…T…" → "YYYY-MM-DD"), for
 * day-bucketing. A bare naive time string (no 'T') is returned date-part as-is.
 * A2 P4: the instant branch is now business-tz (was raw UTC split).
 */
export function extractDate(iso: string): string {
  if (iso.includes('T')) {
    const { year, month, day } = instantToBusinessParts(iso);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  return iso.split('T')[0];
}

/**
 * Fractional hour of a stored instant in the business tz ("…T09:30…" → 9.5), for
 * grid positioning. A bare naive time string (no 'T') parses as-is.
 * A2 P4: the instant branch is now business-tz (was raw UTC split).
 */
export function extractHour(iso: string): number {
  if (iso.includes('T')) {
    const { hour, minute } = instantToBusinessParts(iso);
    return hour + minute / 60;
  }
  const [h, m] = iso.split(':').map(Number);
  return h + (m / 60);
}

/**
 * Format to 12h time. A stored instant ("…T…") renders in the business tz; a bare
 * naive clock string (e.g. business_hours "09:00:00", no 'T') formats as-is.
 * A2 P4: the instant branch is now business-tz (was raw UTC split).
 */
export function formatTime12(iso: string): string {
  if (iso.includes('T')) return formatInstantInTz(iso);
  const [h, m] = iso.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
}

/**
 * Wall-clock date (YYYY-MM-DD) + time (HH:MM[:SS]) in a business tz → true UTC
 * instant (ISO). The admin counterpart to the server's `(date + time) AT TIME
 * ZONE business_tz` (A2 P1) and mobile's `localWallClockToInstant`. Zero-
 * dependency `Intl`. Interpret the wall-clock as UTC, measure the zone's offset
 * at that instant, then subtract it — DST-correct because `Intl` resolves the
 * offset per calendar date.
 */
export function localWallClockToInstant(date: string, time: string, businessTz: string | null): string {
  const tz = businessTz ?? 'America/Toronto';
  const t = time.length === 5 ? `${time}:00` : time;
  const [Y, M, D] = date.split('-').map(Number);
  const [h, m, s] = t.split(':').map(Number);
  const asUTC = Date.UTC(Y, M - 1, D, h, m, s || 0);
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const p = Object.fromEntries(dtf.formatToParts(new Date(asUTC)).map((x) => [x.type, x.value]));
  const localAsUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return new Date(asUTC - (localAsUTC - asUTC)).toISOString();
}

/** Get display name for a user ID */
export function getUserName(userId: string | undefined, users: ScheduleUser[]): string {
  if (!userId) return 'Unassigned';
  const u = users.find(u => u.id === userId);
  return u ? `${u.first_name} ${u.last_name.charAt(0)}.` : 'Unknown';
}

/** Get initials for a user ID */
export function getUserInitials(userId: string | undefined, users: ScheduleUser[]): string {
  if (!userId) return '?';
  const u = users.find(u => u.id === userId);
  return u ? `${u.first_name.charAt(0)}${u.last_name.charAt(0)}`.toUpperCase() : '?';
}

/** Get the entry color — custom color overrides type default */
export function getEntryColor(entry: { type: string; color: string | null }): string {
  return entry.color ?? ENTRY_TYPE_COLORS[entry.type as ScheduleEntryType] ?? '#ADB5BD';
}
